import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import type {
    Form,
    FormField,
    FormSection,
    ReactiveFieldLogicContext,
    ReactiveSectionLogicContext,
} from '../../interfaces/form.interface';
import { InputType, type ReactiveLazyDropdownFilterRequest } from '../../interfaces/input.interface';
import { ReactiveFieldRuntimeState } from '../../interfaces/reactive-field-runtime-state.interface';
import { ReactiveFieldRuntimeStateManager } from '../../model/reactive-field-runtime-state.manager';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';
import { AgriculturalHelpContext, buildAgriculturalFieldHelp } from '../../utils/form-field-help.util';
import { REACTIVE_FORM_BUILDER_ENVIRONMENT } from '../../adapters/reactive-form-builder.adapters';

@Component({
    selector: 'app-reactive-form-sections',
    standalone: false,
    templateUrl: './form-sections.component.html',
    styleUrls: ['./form-sections.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactiveFormSectionsComponent<T> implements OnChanges {
    @Input({ required: true }) public form!: Form<T>;
    @Input({ required: true }) public model!: FormGroup;
    @Input() public originalModel: Record<string, unknown> = {};
    @Input() public filterInputParcelas = false;
    @Input() public externalDropdownsReady = false;
    @Input() public fieldStates: ReadonlyMap<string, ReactiveFieldRuntimeState<T>> = new Map();
    @Input() public renderVersion = 0;
    @Input() public undoEnabled = false;
    @Input() public debug = false;
    @Input() public showFieldHelpButtons = false;
    @Input() public tourAnchorsEnabled = true;

    @Output() public retryDropdown = new EventEmitter<string>();
    @Output() public lazyDropdownRequested = new EventEmitter<string>();

    @Output() public lazyDropdownFilterRequested = new EventEmitter<ReactiveLazyDropdownFilterRequest>();

    private readonly cdr = inject(ChangeDetectorRef);

    private readonly builderEnvironment = inject(REACTIVE_FORM_BUILDER_ENVIRONMENT);

    public requestLazyDropdownLoad(fieldName: string): void {
        const normalizedFieldName = fieldName.trim();

        if (normalizedFieldName === '') {
            return;
        }

        this.lazyDropdownRequested.emit(normalizedFieldName);
    }

    public requestLazyDropdownFilter(request: ReactiveLazyDropdownFilterRequest): void {
        if (request.fieldName.trim() === '' || request.filter.trim() === '') {
            return;
        }

        this.lazyDropdownFilterRequested.emit(request);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['form'] || changes['externalDropdownsReady'] || changes['fieldStates'] || changes['renderVersion']) {
            this.firstVisibleFieldHelpKey = null;
            this.cdr.markForCheck();
        }
    }

    private readonly fieldHelpTourAnchorId = 'farmerTour.fieldHelpButton';

    private firstVisibleFieldHelpKey: string | null = null;

    public getFieldHelpTourAnchorId(field: FormField<T>): string {
        if (!this.tourAnchorsEnabled) {
            return '';
        }

        return this.getFieldKey(field) === this.getFirstVisibleFieldHelpKey() ? this.fieldHelpTourAnchorId : '';
    }

    private getFirstVisibleFieldHelpKey(): string | null {
        if (this.firstVisibleFieldHelpKey !== null) {
            return this.firstVisibleFieldHelpKey;
        }

        for (const section of this.form.sections) {
            if (!this.shouldRenderSection(section)) {
                continue;
            }

            const firstField = section.fields.find((currentField) =>
                this.shouldRenderField(currentField) && this.hasFieldHelp(currentField, section.name)
            );

            if (firstField !== undefined) {
                this.firstVisibleFieldHelpKey = this.getFieldKey(firstField);

                return this.firstVisibleFieldHelpKey;
            }
        }

        return null;
    }

    private hasFieldHelp(field: FormField<T>, sectionName?: string): boolean {
        return Boolean(buildAgriculturalFieldHelp(
            field.name,
            field.label,
            this.getExplicitHelpText(field),
            this.getHelpContext(sectionName)
        ));
    }

    private getFieldKey(field: FormField<T>): string {
        return field.name ?? field.label ?? '';
    }

    private getExplicitHelpText(field: FormField<T>): string | undefined {
    const helpText = field['helpText'];

    return typeof helpText === 'string' ? helpText : undefined;
}

private getHelpContext(sectionName?: string): AgriculturalHelpContext {
    return {
        ...(this.form?.name !== undefined ? { formName: this.form.name } : {}),
        ...(this.form?.header !== undefined ? { formHeader: this.form.header } : {}),
        ...(sectionName !== undefined ? { sectionName } : {}),
    };
}


    public shouldRenderSection(section: FormSection<T>): boolean {
        if (!section.isVisible || !this.evaluateSectionVisibility(section)) {
            return false;
        }

        const hasVisibleFields = section.fields.some((field) => this.shouldRenderField(field));
        const hasVisibleButtons = (section.buttons ?? []).some((button) => button.isVisible !== false);

        return hasVisibleFields || hasVisibleButtons;
    }

    private evaluateSectionVisibility(section: FormSection<T>): boolean {
        const rule = section.visibleWhen;

        if (rule === undefined) {
            return true;
        }

        if (typeof rule === 'boolean') {
            return rule;
        }

        return rule(this.createSectionLogicContext());
    }

    private createSectionLogicContext(): ReactiveSectionLogicContext<T> {
        const rawValue = this.model?.getRawValue() as Record<string, unknown>;

        return {
            model: this.model,
            value: rawValue as Readonly<Partial<T>>,
            getValue: (fieldName: string): unknown => this.model?.get(fieldName)?.value,
            getRawValue: (): Record<string, unknown> => rawValue,
        };
    }

    public shouldRenderField(field: FormField<T>): boolean {
        const isVisible = this.evaluateFieldVisibility(field) &&
            field.input?.type !== InputType.EMPTY &&
            field.input?.type !== InputType.HIDDEN;

        if (isVisible) {
            this.applyFieldLabel(field);
        }

        return isVisible;
    }

    private applyFieldLabel(field: FormField<T>): void {
        const rule = field.labelWhen;

        if (rule === undefined) {
            return;
        }

        const nextLabel = rule.resolve(this.createFieldLogicContext(field));

        if (nextLabel !== field.label) {
            field.label = nextLabel;
        }
    }

    private evaluateFieldVisibility(field: FormField<T>): boolean {
        const rule = field.visibleWhen;

        if (rule === undefined) {
            return field.conditions.isVisible;
        }

        if (typeof rule === 'boolean') {
            return rule;
        }

        const context = this.createFieldLogicContext(field);

        if (typeof rule === 'function') {
            return rule(context);
        }

        return rule.resolve(context);
    }

    private createFieldLogicContext(field: FormField<T>): ReactiveFieldLogicContext<T> {
        const rawValue = this.model.getRawValue() as Record<string, unknown>;

        return {
            field,
            model: this.model,
            changedField: null,
            changeOrigin: 'programmatic',
            currentValue: this.model.get(field.name)?.value,
            getValue: (fieldName: string): unknown => this.model.get(fieldName)?.value,
            getRawValue: (): Record<string, unknown> => rawValue,
        };
    }

    public isFieldReady(field: FormField<T>): boolean {
        return ReactiveFieldRuntimeStateManager.isFieldReady(
            field,
            this.fieldStates,
            this.form.type,
            this.externalDropdownsReady,
        );
    }

    public isFieldErrored(field: FormField<T>): boolean {
        return this.fieldStates.get(field.name ?? '')?.loadState === 'error';
    }

    public getFieldError(field: FormField<T>): string {
        return this.fieldStates.get(field.name ?? '')?.errorMessage ?? "No se han podido cargar las opciones.";
    }

    public hasFieldWarning(field: FormField<T>): boolean {
        return String(this.fieldStates.get(field.name ?? '')?.warningMessage ?? '').trim() !== '';
    }

    public getFieldWarning(field: FormField<T>): string {
        return this.fieldStates.get(field.name ?? '')?.warningMessage ?? '';
    }

    public retryDropdownLoad(field: FormField<T>): void {
        const fieldName = field.name ?? '';

        if (fieldName.trim() !== '') {
            this.retryDropdown.emit(fieldName);
        }
    }

    public getDropdownDebugItems(field: FormField<T>): string[] {
        if (!this.debug || this.builderEnvironment.production) {
            return [];
        }

        const diagnostics = this.fieldStates.get(field.name ?? '')?.diagnostics;

        if (!diagnostics) {
            return [];
        }

        return [
            `state=${diagnostics.loadState}`,
            `cache=${diagnostics.cacheStatus}`,
            `options=${diagnostics.optionCount}/${diagnostics.filteredOptionCount}`,
            `retries=${diagnostics.retryCount}`,
            diagnostics.loadDurationMs !== undefined ? `ms=${diagnostics.loadDurationMs}` : '',
            diagnostics.changedField ? `changed=${diagnostics.changedField}` : '',
            diagnostics.changeOrigin ? `origin=${diagnostics.changeOrigin}` : '',
        ].filter((item) => item !== '');
    }

    public trackBySectionName(index: number, section: { name?: string; order?: number }): string {
        const sectionName = section.name?.trim();

        return sectionName ? sectionName : String(section.order ?? index);
    }

    public trackByFieldName(index: number, field: FormField<T>): string {
        const fieldKey = field.name ?? String(field.order ?? index);

        return `${fieldKey}-${field.input?.type ?? ''}`;
    }

    public trackByTableTitle(index: number, table: { title?: string }): string {
        return table.title ?? String(index);
    }

    public trackByButtonLabel(index: number, button: { label?: string }): string {
        return button.label ?? String(index);
    }

    public getPendingDisplay(field: FormField<T>): string {
        const name = field.name ?? '';

        if (!name) {
            return '';
        }

        const control = this.model?.get(name);

        if (!control) {
            return '';
        }

        return ReactiveFormValueUtils.formatPreviewValue(control.value, field);
    }
}
